import { pick } from '../../Context/dist/effect-Context.esm.js';
import { contextWith } from '../../Effect/dist/effect-Effect.esm.js';
import { RequestResolverTypeId as RequestResolverTypeId$1, isRequestResolver as isRequestResolver$1, resolverLocally } from '../../internal/core.esm.js';
import { make as make$1, makeWithEntry as makeWithEntry$1, makeBatched as makeBatched$1, around as around$1, batchN as batchN$1, mapInputContext as mapInputContext$1, eitherWith as eitherWith$1, fromFunction as fromFunction$1, fromFunctionBatched as fromFunctionBatched$1, fromFunctionEffect as fromFunctionEffect$1, never as never$1, provideContext as provideContext$1, race as race$1 } from '../../internal/dataSource.esm.js';

/**
 * @since 2.0.0
 */
/**
 * @since 2.0.0
 * @category symbols
 */
const RequestResolverTypeId = RequestResolverTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * A `RequestResolver<A, R>` requires an environment `R` and is capable of executing
 * requests of type `A`.
 *
 * Data sources must implement the method `runAll` which takes a collection of
 * requests and returns an effect with a `RequestCompletionMap` containing a
 * mapping from requests to results. The type of the collection of requests is
 * a `Chunk<Chunk<A>>`. The outer `Chunk` represents batches of requests that
 * must be performed sequentially. The inner `Chunk` represents a batch of
 * requests that can be performed in parallel. This allows data sources to
 * introspect on all the requests being executed and optimize the query.
 *
 * Data sources will typically be parameterized on a subtype of `Request<A>`,
 * though that is not strictly necessarily as long as the data source can map
 * the request type to a `Request<A>`. Data sources can then pattern match on
 * the collection of requests to determine the information requested, execute
 * the query, and place the results into the `RequestCompletionMap` using
 * `RequestCompletionMap.empty` and `RequestCompletionMap.insert`. Data
 * sources must provide results for all requests received. Failure to do so
 * will cause a query to die with a `QueryFailure` when run.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * @since 2.0.0
 * @category utils
 */
const contextFromEffect = self => contextWith(_ => provideContext(self, _));

/**
 * @since 2.0.0
 * @category utils
 */
const contextFromServices = (...services) => self => contextWith(_ => provideContext(self, pick(...services)(_)));

/**
 * Returns `true` if the specified value is a `RequestResolver`, `false` otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isRequestResolver = isRequestResolver$1;

/**
 * Constructs a data source with the specified identifier and method to run
 * requests.
 *
 * @since 2.0.0
 * @category constructors
 */
const make = make$1;

/**
 * Constructs a data source with the specified identifier and method to run
 * requests.
 *
 * @since 2.0.0
 * @category constructors
 */
const makeWithEntry = makeWithEntry$1;

/**
 * Constructs a data source from a function taking a collection of requests
 * and returning a `RequestCompletionMap`.
 *
 * @since 2.0.0
 * @category constructors
 */
const makeBatched = makeBatched$1;

/**
 * A data source aspect that executes requests between two effects, `before`
 * and `after`, where the result of `before` can be used by `after`.
 *
 * @since 2.0.0
 * @category combinators
 */
const around = around$1;

/**
 * Returns a data source that executes at most `n` requests in parallel.
 *
 * @since 2.0.0
 * @category combinators
 */
const batchN = batchN$1;

/**
 * Provides this data source with part of its required context.
 *
 * @since 2.0.0
 * @category context
 */
const mapInputContext = mapInputContext$1;

/**
 * Returns a new data source that executes requests of type `C` using the
 * specified function to transform `C` requests into requests that either this
 * data source or that data source can execute.
 *
 * @since 2.0.0
 * @category combinators
 */
const eitherWith = eitherWith$1;

/**
 * Constructs a data source from a pure function.
 *
 * @since 2.0.0
 * @category constructors
 */
const fromFunction = fromFunction$1;

/**
 * Constructs a data source from a pure function that takes a list of requests
 * and returns a list of results of the same size. Each item in the result
 * list must correspond to the item at the same index in the request list.
 *
 * @since 2.0.0
 * @category constructors
 */
const fromFunctionBatched = fromFunctionBatched$1;

/**
 * Constructs a data source from an effectual function.
 *
 * @since 2.0.0
 * @category constructors
 */
const fromFunctionEffect = fromFunctionEffect$1;

/**
 * A data source that never executes requests.
 *
 * @since 2.0.0
 * @category constructors
 */
const never = never$1;

/**
 * Provides this data source with its required context.
 *
 * @since 2.0.0
 * @category context
 */
const provideContext = provideContext$1;

/**
 * Returns a new data source that executes requests by sending them to this
 * data source and that data source, returning the results from the first data
 * source to complete and safely interrupting the loser.
 *
 * @since 2.0.0
 * @category combinators
 */
const race = race$1;

/**
 * Returns a new data source with a localized FiberRef
 *
 * @since 2.0.0
 * @category combinators
 */
const locally = resolverLocally;

export { RequestResolverTypeId, around, batchN, contextFromEffect, contextFromServices, eitherWith, fromFunction, fromFunctionBatched, fromFunctionEffect, isRequestResolver, locally, make, makeBatched, makeWithEntry, mapInputContext, never, provideContext, race };
